import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  ActionIcon,
  Icon,
  inkA,
  ModalOverlay,
  orange,
  PlainButton,
  SearchInput,
  SkeletonBar,
  tokens,
} from 'components/v3';
import { useBuilder } from './BuilderContext';
import {
  savedFieldDetail,
  savedTypeIcon,
  savedTypeLabel,
} from './state/savedFields';

// The saved-field library (design: SavedFieldsModal): search by name or
// type, tap one to add it to the form, trash to remove it from the
// library. Esc or the backdrop closes it.

const Pick = styled(PlainButton)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  text-align: left;
  color: ${(p) => p.$color};

  &:hover {
    background: ${orange(0.08)};
  }
`;

export const filterSaved = (list, query) => {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return list;
  return list.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      savedTypeLabel(s).toLowerCase().includes(q)
  );
};

const titleId = 'fb-saved-fields-title';

const SavedFieldsModal = ({ open, onClose, onPick }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { savedFields, savedFieldsLoading, removeSavedField } = useBuilder();
  const [query, setQuery] = useState('');
  const items = filterSaved(savedFields, query);
  const q = query.trim();

  const body = () => {
    if (savedFieldsLoading && !savedFields.length) {
      return (
        <div
          style={{ display: 'grid', gap: 10, padding: 8 }}
          aria-hidden="true"
        >
          <SkeletonBar $w="80%" $h="16px" />
          <SkeletonBar $w="60%" $h="16px" />
        </div>
      );
    }
    if (!items.length) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            padding: '36px 0',
            fontSize: 13,
            color: t.faint,
          }}
        >
          <Icon
            name={q ? 'search' : 'bookmark'}
            size={28}
            color="currentColor"
          />
          {q ? `No saved fields match “${q}”` : 'Nothing saved yet'}
        </div>
      );
    }
    return (
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'grid',
          gap: 4,
        }}
      >
        {items.map((saved) => (
          <li
            key={saved.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 8,
              border: '1px solid ' + t.border,
              background: inkA(theme, 0.03),
            }}
          >
            <Pick
              data-help="palette-saved-field"
              onClick={() => onPick(saved)}
              title="Add to form"
              $color={t.text}
            >
              <Icon name={savedTypeIcon(saved)} size={16} color={orange(1)} />
              <span style={{ minWidth: 0 }}>
                <span
                  style={{ display: 'block', fontSize: 13, fontWeight: 600 }}
                >
                  {saved.name}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 11,
                    color: t.faint,
                    marginTop: 1,
                  }}
                >
                  {savedFieldDetail(saved)}
                </span>
              </span>
            </Pick>
            <ActionIcon
              icon="trash-2"
              danger
              title={`Remove “${saved.name}” from Saved Fields`}
              onClick={() => removeSavedField(saved)}
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <ModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      placement="center"
      zIndex={1150}
      panelStyle={{
        width: 'min(520px, calc(100vw - 32px))',
        maxHeight: 'min(640px, calc(100vh - 48px))',
        display: 'flex',
        flexDirection: 'column',
        background: t.card,
        color: t.text,
        border: '1px solid ' + t.border,
        borderRadius: 14,
        boxShadow: t.shadowPop,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 18px 12px',
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: orange(0.14),
            color: orange(1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="bookmark" size={17} color="currentColor" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            Saved Fields
          </h2>
          <div style={{ fontSize: 12, color: t.faint }}>
            {`${savedFields.length} saved · tap one to add it to the form`}
          </div>
        </div>
        <ActionIcon icon="x" title="Close" onClick={onClose} />
      </div>
      <div style={{ padding: '0 18px 12px' }}>
        <SearchInput
          placeholder="Search saved fields…"
          aria-label="Search saved fields"
          onChange={setQuery}
        />
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '0 12px 12px',
        }}
      >
        {body()}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          padding: '10px 18px 14px',
          borderTop: '1px solid ' + t.border,
          fontSize: 12,
          lineHeight: 1.5,
          color: t.faint,
        }}
      >
        <Icon name="info" size={14} color="currentColor" />
        <span>
          Save any field for reuse from its settings panel — select it on the
          form and choose <b>Save to Saved Fields</b>.
        </span>
      </div>
    </ModalOverlay>
  );
};

SavedFieldsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

export default SavedFieldsModal;
